import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { Loader2, HandHeart, Clock, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function StudentSocialWork() {
  const { user, formatApiError } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", college: "", stream: "", motivation: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/public/social-work"),
        api.get("/student/social-work/my-applications"),
      ]);
      setPrograms(p.data || []);
      setApps(a.data || []);
    } finally { setLoading(false); }
  };

  const openApply = (p) => {
    setSelected(p);
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      college: user?.college || "",
      stream: user?.stream || "",
      motivation: "",
    });
  };

  const apply = async () => {
    setSubmitting(true);
    try {
      await api.post("/student/social-work/apply", { program_id: selected.id, ...form });
      toast.success("Application submitted!");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold flex items-center gap-2" data-testid="social-work-heading">
        <HandHeart className="text-amber-500" size={26} /> Social Work Programs
      </h1>
      <p className="text-slate-500 text-sm mt-1">Contribute your hours to community work and earn an official Certificate of Appreciation.</p>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>
      ) : (
        <>
          {programs.length === 0 ? (
            <div className="mt-8 p-14 rounded-2xl border border-dashed border-slate-300 text-center">
              <HandHeart size={40} className="mx-auto text-slate-300" />
              <div className="mt-3 text-slate-500">No social-work programmes open right now.</div>
            </div>
          ) : (
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {programs.map((p) => (
                <div key={p.id} className="p-6 rounded-2xl border border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm transition" data-testid={`socwork-card-${p.id}`}>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-bold flex items-center gap-1.5">
                    <Clock size={12} /> {p.duration_hours || 40} HOURS
                  </div>
                  <h3 className="heading text-xl font-bold mt-2">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.description}</p>
                  {p.location && <div className="mt-3 text-xs text-slate-500">Location: {p.location}</div>}
                  <button
                    onClick={() => openApply(p)}
                    className="mt-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full px-5 py-2 text-sm"
                    data-testid={`apply-socwork-${p.id}-button`}
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          )}

          {apps.length > 0 && (
            <div className="mt-10">
              <h2 className="heading text-lg font-bold">My Social Work Applications</h2>
              <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="p-3 font-semibold">Program</th>
                      <th className="p-3 font-semibold">Status</th>
                      <th className="p-3 font-semibold">Attendance</th>
                      <th className="p-3 font-semibold">Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100" data-testid={`my-socwork-${a.id}`}>
                        <td className="p-3">{a.program_title}</td>
                        <td className="p-3">
                          <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-red-100 text-red-700" : a.status === "completed" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="p-3">
                          {a.attendance_url ? (
                            <a href={ABS(a.attendance_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1 text-xs">
                              <FileText size={13} /> Sheet
                            </a>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="p-3">
                          {a.certificate_url ? (
                            <a href={ABS(a.certificate_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1" data-testid={`my-socwork-cert-${a.id}`}>
                              <Download size={14} /> {a.certificate_no || "Download"}
                            </a>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Apply: {selected?.title}</DialogTitle>
            <DialogDescription>Tell us about yourself — we'll review your application within 48 hours.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            {[
              ["Full Name *", "name"],
              ["Email *", "email"],
              ["Phone *", "phone"],
              ["College", "college"],
              ["Stream / Specialisation", "stream"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
                <input
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none"
                  data-testid={`apply-socwork-${key}-input`}
                />
              </div>
            ))}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Why this programme?</label>
              <textarea
                rows={3}
                value={form.motivation}
                onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none resize-none"
                data-testid="apply-socwork-motivation-input"
              />
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-100 flex-row justify-end gap-2">
            <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm font-semibold" data-testid="apply-socwork-cancel">Cancel</button>
            <button
              onClick={apply}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg px-5 py-2 text-sm disabled:opacity-60"
              data-testid="apply-socwork-submit"
            >
              {submitting ? "Applying..." : "Submit Application"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
